import { X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { useShellLayout } from './Layout';
import s from './Sheet.module.css';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** 'auto' = bottom sheet on phones, side sheet from the right on wider screens. */
  side?: 'auto' | 'bottom' | 'right' | 'left';
  size?: 'sm' | 'md' | 'lg';
  /** Show the title visually (it is always announced). */
  showTitle?: boolean;
  modal?: boolean;
  /** No body padding (for content that brings its own, like the assistant). */
  flush?: boolean;
  /** Content that has its own close control can drop the sheet's. */
  closeButton?: boolean;
  className?: string;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = 'auto',
  size = 'md',
  showTitle = true,
  modal = true,
  flush,
  closeButton = true,
  className,
}: SheetProps) {
  const layout = useShellLayout();
  const resolved = side === 'auto' ? (layout === 'phone' ? 'bottom' : 'right') : side;
  return (
    <D.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <D.Portal>
        {modal && <D.Overlay className={s.overlay} />}
        <D.Content className={cx(s.sheet, s[resolved], s[size], className)}>
          {resolved === 'bottom' && <div className={s.grip} aria-hidden />}
          <header className={cx(s.header, !showTitle && s.headerBare)}>
            <div className={cx(s.heading, !showTitle && 'sr-only')}>
              <D.Title className={s.title}>{title}</D.Title>
              {description ? (
                <D.Description className={s.description}>{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{title}</D.Description>
              )}
            </div>
            {closeButton && (
              <D.Close className={s.close} aria-label={t('Close')}>
                <X aria-hidden />
              </D.Close>
            )}
          </header>
          <div className={cx(s.body, flush && s.flush)}>{children}</div>
          {footer && <footer className={s.footer}>{footer}</footer>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
